/**
 * PodiumList
 *
 * Sub-component: renders the podium entries with all branching states
 * (disabled, loading, error, empty, populated). Styled to match the Leaderboard
 * list (BUG-20260610-02) — shared `RankCard` rows via `getRankPreset`, with the
 * ex-aequo (tied) marker and current-user highlight.
 *
 * Branching lives here — not in the Page View.
 * Single Responsibility: display the podium body, including ex-aequo treatment.
 */

import type { TFunction } from 'i18next';
import { RankCard, getRankPreset } from '@/shared/components/RankCard';
import type { PodiumEntry } from '../types';

interface PodiumListProps {
  entries: PodiumEntry[] | undefined;
  isLoading: boolean;
  isEnabled: boolean;
  error: Error | null;
  t: TFunction;
  /** What each row's score shows: reward points (+ coin) or today's match count.
   *  Defaults to 'points' (closed-day winners view). */
  metric?: 'points' | 'matches';
  /** Highlight the caller's own row when their pseudo appears in the podium */
  currentUserPseudo?: string;
  /** The caller's own standing when they finished outside the palier — shown
   *  after a "…" separator (SOKA-provided; see live-personal-rank-gap doc). */
  currentUserEntry?: PodiumEntry;
}

function PodiumStateMessage({ children }: { children: React.ReactNode }) {
  return (
    <p
      className="mt-6 text-center font-mind-boggle opacity-70"
      style={{ color: '#1A3A5C', fontSize: 'clamp(0.75rem, 3vw, 1rem)' }}
    >
      {children}
    </p>
  );
}

export default function PodiumList({
  entries,
  isLoading,
  isEnabled,
  error,
  t,
  metric = 'points',
  currentUserPseudo,
  currentUserEntry
}: PodiumListProps) {
  if (!isEnabled) {
    return <PodiumStateMessage>{t('dailyRewards.claim.disabled')}</PodiumStateMessage>;
  }

  if (isLoading) {
    return (
      <p
        className="mt-6 text-center font-mind-boggle opacity-70 animate-pulse"
        style={{ color: '#1A3A5C', fontSize: 'clamp(0.75rem, 3vw, 1rem)' }}
      >
        {t('common.loading')}
      </p>
    );
  }

  if (error) {
    return (
      <div role="alert" className="mt-6 text-center text-red-500 font-mind-boggle">
        {t('errorPage.message')}
      </div>
    );
  }

  if (!entries || entries.length === 0) {
    return <PodiumStateMessage>{t('dailyRewards.podium.empty')}</PodiumStateMessage>;
  }

  // Show the caller's own row only when they aren't already among the winners.
  const callerInPodium = !!currentUserPseudo && entries.some((e) => e.pseudo === currentUserPseudo);
  const showCallerTail = !!currentUserEntry && !callerInPodium;

  return (
    <ol className="mt-2 flex flex-col gap-2">
      {entries.map((entry, idx) => (
        <PodiumEntryRow
          key={`${String(entry.rank)}-${String(idx)}`}
          entry={entry}
          metric={metric}
          matchesLabel={t('dailyRewards.podium.matchesLabel')}
          isCurrentUser={!!currentUserPseudo && entry.pseudo === currentUserPseudo}
        />
      ))}

      {/* "winners … you" — the caller's own standing when outside the palier */}
      {showCallerTail && (
        <>
          <li
            aria-hidden="true"
            className="list-none text-center font-mind-boggle leading-none select-none"
            style={{ color: '#1A3A5C' }}
          >
            …
          </li>
          <PodiumEntryRow
            entry={currentUserEntry}
            metric={metric}
            matchesLabel={t('dailyRewards.podium.matchesLabel')}
            isCurrentUser
          />
        </>
      )}
    </ol>
  );
}

// ---- PodiumEntryRow — a single rank entry rendered as a leaderboard RankCard ----

interface PodiumEntryRowProps {
  entry: PodiumEntry;
  metric: 'points' | 'matches';
  matchesLabel: string;
  isCurrentUser: boolean;
}

function PodiumEntryRow({ entry, metric, matchesLabel, isCurrentUser }: PodiumEntryRowProps) {
  const isMatches = metric === 'matches';
  return (
    <li data-tied={entry.tied ? 'true' : 'false'} className="relative list-none">
      <RankCard
        rank={entry.rank}
        username={entry.pseudo}
        score={isMatches ? entry.validMatches : entry.amount}
        scoreLabel={isMatches ? matchesLabel : undefined}
        background={getRankPreset(entry.rank)}
        isHighlighted={isCurrentUser}
      />
      {/* Ex-aequo marker (AC 02-03: tied entries visually marked as shared) */}
      {entry.tied && (
        <span
          data-testid="podium-tied-marker"
          className="absolute -top-1 -left-1 font-mind-boggle text-xs px-1.5 rounded-full bg-amber-400 text-black border border-black leading-tight"
          aria-label="ex-aequo"
        >
          =
        </span>
      )}
    </li>
  );
}
